import React, {useEffect, useState} from 'react';
import {useNavigate, Link, useLocation} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {authApi} from '../../services/authApi';
import {enterpriseApi, EnterpriseInviteClaim} from '../../services/enterpriseApi';
import {loginSuccess} from '../../store/slices/authSlice';
import {setUser} from '../../store/slices/userSlice';
import './Auth.css';

const Register: React.FC = () => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteClaim, setInviteClaim] = useState<EnterpriseInviteClaim | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inviteToken = new URLSearchParams(location.search).get('enterpriseInvite')?.trim() || '';

  useEffect(() => {
    if (!inviteToken) {
      setInviteClaim(null);
      setInviteError(null);
      setInviteLoading(false);
      return;
    }

    let isMounted = true;
    setInviteLoading(true);
    setInviteError(null);

    enterpriseApi.claimInvite(inviteToken)
      .then((claim) => {
        if (!isMounted) {
          return;
        }

        setInviteClaim(claim);
        setEmail(claim.email);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        setInviteClaim(null);
        setInviteError(error instanceof Error ? error.message : 'Failed to validate Enterprise invite.');
      })
      .finally(() => {
        if (isMounted) {
          setInviteLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [inviteToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      alert('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    
    try {
      const response = await authApi.register(name.trim(), email.trim(), password);

      if (inviteToken) {
        dispatch(loginSuccess(response.token));
        dispatch(setUser(response.user));

        try {
          await enterpriseApi.acceptInvite(response.token, inviteToken);
          const refreshedUser = await authApi.getCurrentUser(response.token);
          dispatch(setUser(refreshedUser));
          setLoading(false);
          navigate('/settings?enterprise=joined');
          return;
        } catch (acceptError) {
          const refreshedUser = await authApi.getCurrentUser(response.token).catch(() => response.user);
          dispatch(setUser(refreshedUser));
          setLoading(false);
          alert(authApi.getErrorMessage(acceptError));
          navigate('/settings');
          return;
        }
      }

      setLoading(false);
      alert('Registration successful! Please sign in.');
      navigate('/login');
    } catch (error) {
      const message = authApi.getErrorMessage(error);
      setLoading(false);
      alert(message);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Create Account</h1>
        <p className="auth-subtitle">
          {inviteClaim
            ? `Join ${inviteClaim.organizationName} on XPSR`
            : 'Join XPSR today'}
        </p>

        {inviteLoading ? (
          <div className="auth-invite-banner">Checking your Enterprise invite…</div>
        ) : null}

        {inviteClaim ? (
          <div className="auth-invite-banner auth-invite-banner-success">
            <strong>{inviteClaim.organizationName}</strong> invited you as a <strong>{inviteClaim.role}</strong>.
            {inviteClaim.hasExistingUser ? ' This email already has an XPSR account. You can sign in instead.' : null}
          </div>
        ) : null}

        {inviteError ? (
          <div className="auth-invite-banner auth-invite-banner-error">{inviteError}</div>
        ) : null}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <input
            type="text"
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            required
          />
          
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            disabled={Boolean(inviteClaim)}
            required
          />
          
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            required
          />
          
          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input"
            required
          />
          
          <button
            type="submit"
            className="button button-primary auth-button"
            disabled={loading}>
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>
        
        <p className="auth-link">
          Already have an account? <Link to={inviteToken ? `/login?enterpriseInvite=${inviteToken}` : '/login'}>Sign In</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
